import React, { useCallback } from "react";
import {
  Dropdown,
  Icon,
  Sidebar as MauiSidebar,
  Tooltip,
} from "@findemdev/maui";
import type { MenuInfo } from "@findemdev/maui";
import { getMenuItem } from "./constants";
import UserContext from "../../contexts/UserContext";
import {
  getEnabledSidebarItems,
  ISidebarMenuItem,
} from "../../common/utils/navigationUtil";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getInitials, useUrlParams } from "@findemdev/findem-helpers";
import Intercom from "@intercom/messenger-js-sdk";
import { getAppName } from "../../utils/common";
import { APP_NAMES } from "../../types/app";
import {
  PoweredByWrapper,
  ExtPromotorContainer,
  CollapsedExtensionButton,
  CollapsedExtensionContainer,
  CollapsedExtensionIcon,
  ModalContent,
  ModalOverlay,
  SidebarAvatar,
  SidebarFooter,
  SidebarMenuEmail,
  SidebarMenuLabelInfo,
  SidebarMenuUserInfo,
  SidebarUserButton,
  SidebarUserText,
} from "./Sidebar.styled";
import ExtensionPromotion from "../Common/ExtensionPromotion";
import ExtensionArtworkSmall from "../../assets/images/Extension graphic-collapsed.png";
import { EXTERNAL_URLS, HELP_URLS } from "../../constants/urls";
import PoweredByFindemLogo from "../Common/PoweredByFindemLogo/PoweredByFindemLogo";
import useProfileColor from "../../customHooks/useProfileColors";
import { useLogoutUserMutation } from "../../services/apis";
import RraLogoUrl from "../../assets/images/svgs/rra-login-logo.svg?url";
import RraMarkUrl from "../../assets/images/svgs/rra-mark.svg?url";
import FindemLogoUrl from "../../assets/images/svgs/findem-logo-redesigned.svg?url";
import FindemMarkUrl from "../../assets/images/svgs/findem-logo-black-small.svg?url";

interface ISidebarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const appName = getAppName();

// Maui Sidebar's logo API spells this property as "dimnensions".
const sidebarLogo = appName === APP_NAMES.RRA
  ? {
      collapsed: {
        src: RraMarkUrl,
        dimnensions: {
          height: "32px",
          width: "32px",
        },
      },
      expanded: {
        src: RraLogoUrl,
        dimnensions: {
          height: "32px",
        },
      },
      altText: "Russell Reynolds Associates",
    }
  : {
      collapsed: {
        src: FindemMarkUrl,
      },
      expanded: {
        src: FindemLogoUrl,
      },
      altText: "Findem",
    };

const Sidebar = (props: ISidebarProps) => {
  const { isSidebarCollapsed, setIsSidebarCollapsed } = props;
  const { isTesting } = useUrlParams();
  const userModel = React.useContext(UserContext);
  const [logoutUser] = useLogoutUserMutation();
  const [showExtensionModal, setShowExtensionModal] = React.useState(false);
  const [isExtensionPromotionDismissed, setIsExtensionPromotionDismissed] = React.useState(false);
  const user = userModel.getUser();
  const userColor = useProfileColor(user?.name || "");

  const location = useLocation();
  const navigate = useNavigate();

  // const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);

  const sidebarItems: ISidebarMenuItem[] = React.useMemo(() => {
    return getEnabledSidebarItems(userModel.getUser(), !!isTesting);
  }, [userModel]);

  const menuItems = React.useMemo(() => {
    return sidebarItems.map((item: ISidebarMenuItem) => {
      if (item.children?.length) {
        return getMenuItem(
          item.name,
          item.path.split("/")[1],
          <Icon icon={item.icon} $size={18} />,
          item.children.map((child) =>
            getMenuItem(
              <Link to={child.path} data-link={child.path}>
                {child.name}
              </Link>,
              child.path.split("/")[1],
              <Icon icon={child.icon} $size={18} />
            )
          )
        );
      }
      return getMenuItem(
        <Link to={item.path} data-link={item.path}>
          {item.name}
        </Link>,
        item.path.split("/")[1],
        <Icon icon={item.icon} $size={18} />
      );
    });
  }, [sidebarItems]);

  const userMenuOptions = React.useMemo(() => {
    return [
      {
        key: "profile_info",
        label: (
          <SidebarMenuUserInfo>
            <SidebarAvatar $bgColor={userColor}>
              {getInitials(user?.name || "")}
            </SidebarAvatar>
            <div>
              <div>
                <strong>{user?.name}</strong>
              </div>
              <SidebarMenuEmail>{user?.email}</SidebarMenuEmail>
            </div>
          </SidebarMenuUserInfo>
        ),
      },
      {
        type: "divider",
      },
      ...(appName === APP_NAMES.RRA
        ? [
            {
              key: "help",
              label: (
                <SidebarMenuLabelInfo>
                  <Icon icon="help" $size={14} />
                  Help
                </SidebarMenuLabelInfo>
              ),
            },
            {
              key: "submit_request",
              label: (
                <SidebarMenuLabelInfo>
                  <Icon icon="rate_review" $size={14} />
                  Submit a request
                </SidebarMenuLabelInfo>
              ),
            },
          ]
        : []),
      ...(appName === APP_NAMES.ESP
        ? [
            {
              key: "personal",
              label: <Link to="/account-management">Account Management</Link>,
            },
            {
              key: "help",
              label: (
                <SidebarMenuLabelInfo>
                  <Icon icon="help" $size={14} />
                  Help
                </SidebarMenuLabelInfo>
              ),
            },
            {
              key: "privacy",
              label: (
                <a href={EXTERNAL_URLS.PRIVACY_POLICY} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              ),
            },
            {
              key: "security",
              label: (
                <a href={EXTERNAL_URLS.SECURITY_ISSUE} target="_blank" rel="noreferrer">
                  Report Security Issue
                </a>
              ),
            },
            {
              key: "bottom-divider",
              type: "divider",
            },
            {
              type: "divider",
            },
          ]
        : []),
      {
        key: "logout",
        label: (
          <SidebarMenuLabelInfo>
            <Icon icon="logout" $size={14} />
            Log out
          </SidebarMenuLabelInfo>
        ),
      },
    ];
  }, [user?.email, user?.name, userColor]);

  const handleCustomPathNameLogic = useCallback(
    (pathName: string) => {
      const _pathName: string = location.pathname.replace("/", "");
      if (pathName.startsWith("rra-universe")) {
        return "rra";
      } else if (pathName.startsWith("roadmap")) {
        return "roadmap";
      } else if (pathName.startsWith("apps-catalog")) {
        return "apps-catalog";
      } else if (pathName.startsWith("my-apps")) {
        return "my-apps";
      }
      return _pathName;
    },
    [location.pathname]
  );

  // intercom
  React.useEffect(() => {
    if (userModel.getUser() && appName === APP_NAMES.RRA) {
      const user = userModel.getUser();
      Intercom({
        app_id: "lxdw5ft0",
        user_id: `findem-${user.uid}`,
        name: user.name,
        email: user.email,
      });
    }
  }, [userModel]);
  // add post message listener to handle sidebar collapse
  React.useLayoutEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "searchPageMsg") {
        setIsSidebarCollapsed(true);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [setIsSidebarCollapsed]);

  const goToFindem = useCallback(() => {
    window.open(EXTERNAL_URLS.FINDEM_WEBSITE, "_blank");
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logoutUser().unwrap();
    } finally {
      window.location.href = "/login";
    }
  }, [logoutUser]);

  const handleMenuClick = useCallback(
    (ev: MenuInfo) => {
      if (ev.key === "profile_info") {
        return;
      }

      if (ev.key === "logout") {
        handleLogout();
      }

      if (ev.key === "submit_request") {
        window.open(HELP_URLS.SUBMIT_REQUEST, "_blank");
      }

      if (ev.key === "help") {
        window.open(
          appName === APP_NAMES.RRA
            ? HELP_URLS.RRA_LMS
            : HELP_URLS.EXEC_HELP_CENTER,
          "_blank"
        );
      }
    },
    [handleLogout]
  );

  const handleLogoClick = useCallback(() => {
    navigate("/my-apps");
  }, [navigate]);

  const handleDismissExtensionPromotion = () => {
    setIsExtensionPromotionDismissed(true);
  };

  const handleSmallExtensionClick = useCallback(() => {
    setShowExtensionModal(true);
  }, []);

  const handleCloseExtensionModal = () => {
    setShowExtensionModal(false);
  };

  return (
    <>
      <MauiSidebar
        logo={sidebarLogo}
        onLogoClick={handleLogoClick}
        theme="light"
        collapsed={isSidebarCollapsed}
        onCollapseChange={setIsSidebarCollapsed}
        expandedWidth={220}
        collapsedWidth={60}
        menuProps={{
          customPathNameLogic: handleCustomPathNameLogic,
          menuItems: menuItems,
        }}
        extraContentAfter={
          <SidebarFooter>
            {!isSidebarCollapsed && !isExtensionPromotionDismissed ? (
              <ExtPromotorContainer>
                <ExtensionPromotion onClose={handleDismissExtensionPromotion} />
              </ExtPromotorContainer>
            ) : null}
            {isSidebarCollapsed && !isExtensionPromotionDismissed ? (
              <CollapsedExtensionContainer>
                <Tooltip title="Install Extension" placement="right">
                  <CollapsedExtensionButton
                    type="button"
                    aria-label="Install Extension"
                    onClick={handleSmallExtensionClick}
                  >
                    <CollapsedExtensionIcon
                      src={ExtensionArtworkSmall}
                      alt="Extension"
                    />
                  </CollapsedExtensionButton>
                </Tooltip>
              </CollapsedExtensionContainer>
            ) : null}
            {user?.name ? (
              <Dropdown
                menu={{
                  // @ts-expect-error Maui Dropdown accepts divider items at runtime.
                  items: userMenuOptions,
                  onClick: handleMenuClick,
                }}
                trigger={["click"]}
              >
                <SidebarUserButton
                  type="button"
                  $collapsed={isSidebarCollapsed}
                  aria-label="User menu"
                >
                  <SidebarAvatar $bgColor={userColor}>
                    {getInitials(user?.name || "")}
                  </SidebarAvatar>
                  {!isSidebarCollapsed ? (
                    <SidebarUserText>
                      <strong>{user?.name}</strong>
                      <span>{user?.email}</span>
                    </SidebarUserText>
                  ) : null}
                </SidebarUserButton>
              </Dropdown>
            ) : null}
            {!isSidebarCollapsed ? (
              <Tooltip title="Powered by Findem" placement="right">
                <PoweredByWrapper
                  type="button"
                  aria-label="Powered by Findem"
                  onClick={goToFindem}
                >
                  <PoweredByFindemLogo $size="sm" />
                </PoweredByWrapper>
              </Tooltip>
            ) : null}
          </SidebarFooter>
        }
      />
      {showExtensionModal ? (
        <ModalOverlay onClick={() => setShowExtensionModal(false)}>
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ExtensionPromotion onClose={handleCloseExtensionModal} />
          </ModalContent>
        </ModalOverlay>
      ) : null}
    </>
  );
};

export default Sidebar;
